/**
 * NxFloatingButton -- Boton flotante (FAB) global esquina inferior derecha.
 * Click abre el modal "Nueva nota" del NotesView, autocompletando
 * `related_artefact` desde el hash actual del router (ej. #/auditoria -> auditoria).
 *
 * Uso (en main.js, despues de setupNavBar):
 *   import { setupFloatingNotesButton } from './presentation/components/NxFloatingButton.js';
 *   setupFloatingNotesButton();
 *
 * Coordinacion con NotesView:
 *   - Si la vista actual NO es #/notes: setea window._nxNotesAutoCreate y navega a #/notes.
 *     NotesView al montarse detecta la flag y abre el modal con el related preseted.
 *   - Si ya estamos en #/notes: dispatcha un CustomEvent('nx-notes:open-create')
 *     que el NotesView esta escuchando para abrir el modal sin re-navegar.
 *
 * Visibilidad:
 *   - Hidden en rutas publicas (login / onboard) y cuando NO hay token.
 *
 * @module presentation/components/NxFloatingButton
 */

import { AppConfig } from '../../config.js';

const FAB_ID = 'nx-fab-notes';
const HIDDEN_HASH_PREFIXES = ['#/onboard', '#/login'];

export function setupFloatingNotesButton() {
  if (document.getElementById(FAB_ID)) return; // idempotente

  const btn = document.createElement('button');
  btn.id = FAB_ID;
  btn.type = 'button';
  btn.title = 'Nueva nota — Ctrl+Shift+N · captura bug / mejora / idea desde donde estes';
  btn.setAttribute('aria-label', 'Crear nueva nota');
  btn.innerHTML = '<span style="font-size:1.5rem; line-height:1;">📝</span>' +
                  '<span style="position:absolute; bottom:-4px; right:-2px; background:#238636; color:#fff; ' +
                  'border-radius:50%; width:20px; height:20px; display:flex; align-items:center; ' +
                  'justify-content:center; font-size:0.875rem; font-weight:bold; border:2px solid #161b22;">+</span>';
  btn.style.cssText = [
    'position:fixed', 'bottom:24px', 'right:24px',
    'width:56px', 'height:56px', 'border-radius:50%',
    'background:#1f6feb', 'color:#fff', 'border:none',
    'cursor:pointer', 'display:flex', 'align-items:center', 'justify-content:center',
    'box-shadow:0 6px 16px rgba(0,0,0,0.5), 0 0 0 2px rgba(31,111,235,0.25)',
    'z-index:999',
    'transition:transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease'
  ].join(';');

  btn.addEventListener('mouseover', () => {
    if (btn._enCabecera) return;
    btn.style.transform = 'scale(1.08)';
    btn.style.background = '#388bfd';
    btn.style.boxShadow = '0 8px 20px rgba(0,0,0,0.55), 0 0 0 3px rgba(56,139,253,0.35)';
  });
  btn.addEventListener('mouseout', () => {
    if (btn._enCabecera) return;
    btn.style.transform = 'scale(1)';
    btn.style.background = '#1f6feb';
    btn.style.boxShadow = '0 6px 16px rgba(0,0,0,0.5), 0 0 0 2px rgba(31,111,235,0.25)';
  });

  btn.addEventListener('click', () => {
    const hash = window.location.hash || '';
    const relatedArtefact = (hash.replace(/^#\//, '').split(/[\/?]/)[0] || 'general').toLowerCase();
    if (hash.startsWith('#/notes')) {
      // Ya estamos en notes -> dispatch event para abrir modal sin re-navegar
      window.dispatchEvent(new CustomEvent('nx-notes:open-create', { detail: { relatedArtefact } }));
    } else {
      window._nxNotesAutoCreate = { relatedArtefact };
      window.location.hash = '#/notes';
    }
  });

  // Atajo Ctrl+Shift+N para abrir nota rapido (recordable + no colisiona con browser)
  const keyHandler = (e) => {
    if (e.ctrlKey && e.shiftKey && (e.key === 'N' || e.key === 'n')) {
      e.preventDefault();
      btn.click();
    }
  };
  document.addEventListener('keydown', keyHandler);
  btn._keyHandler = keyHandler; // para cleanup futuro si fuera necesario

  // FRONT-OBSTRUCCIONES-T1 (opcion A, Ares #30261): con cabecera, el boton vive en ella antes de "Salir" -- deja de
  // flotar sobre tarjetas y acciones (medido 2.2.636 en kiosco, inicio y armador). Sin cabecera, como antes.
  const $acciones = document.querySelector('#nx-nav .nav-actions');
  if ($acciones) {
    btn.className = 'btn-ghost btn-sm nx-nota-cabecera';
    btn.style.cssText = '';
    btn.innerHTML = '&#128221; Nota';
    btn._enCabecera = true;
    $acciones.insertBefore(btn, $acciones.querySelector('#btn-logout'));
  } else {
    document.body.appendChild(btn);
  }

  const updateVisibility = () => {
    const hash = window.location.hash || '';
    const isPublic = HIDDEN_HASH_PREFIXES.some(p => hash.startsWith(p));
    const noToken = !AppConfig.token;
    btn.style.display = (isPublic || noToken) ? 'none' : (btn._enCabecera ? '' : 'flex');
  };
  window.addEventListener('hashchange', updateVisibility);
  updateVisibility();
}
